import { Fragment, useState, useEffect } from 'react';
import Layout from '../../../components/layout';
import { approveBillingRateById, deleteBillingRate, getPendingBillingRatesByUserId } from '../../../api/services/billingRateService';
import billingRate from '../../../api/models/billingRate';
import { useAsync } from 'react-async';
import DataTable from '../../../components/dataTable';
import moment from 'moment';
import { useAppSelector } from '../../../app/hooks';
import EditModal from '../../../components/editModal';
import LayoutError from '../../../components/layoutError';
import { Notification } from '../../../components/notifications';
import RejectWithFeedbackRequest from '../../../api/requests/rejectWithFeedbackRequest';

//Async fetch functions
const fetchPendingRates = async (args: any) => await getPendingBillingRatesByUserId(Number(args[0]));
const approveRate = async (args: any) => await approveBillingRateById(Number(args[0]));
const rejectRate = async (args: any) => {
  const request: RejectWithFeedbackRequest = {
    feedback: args[1],
  };
  return await deleteBillingRate(Number(args[0]), request);
}

const ApproveRates = () => {
  const currentUser = useAppSelector(x => x.user);
  //const location = useLocation();
  const [pendingRates, setPendingRates] = useState<billingRate[]>([]);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [openRejectConfirmModal, setOpenRejectConfirmModal] = useState<boolean>(false);
  const [selectedRateId, setSelectedRateId] = useState<number>(-1);
  const [feedback, setFeedback] = useState<string>("");

  //Get the pending rates
  const pendingRatesData = useAsync({
    deferFn: fetchPendingRates,
    onResolve: (r) => setPendingRates(r.data)
  });
  useEffect(() => pendingRatesData.run(currentUser.id), [currentUser]);

  //Columns for our datatable
  const columns = () => [
    {
      Header: 'District',
      accessor: (s: any) => {
        const br: billingRate = s;
        return br.district?.name ?? 'N/A';
      },
      disableSortBy: true,
      sortable: false,
    },
    {
      Header: 'Discipline',
      accessor: (s: any) => {
        const br: billingRate = s;
        return br.discipline?.name ?? 'N/A';
      },
      disableSortBy: true,
      sortable: false,
    },
    {
      Header: 'Rate',
      accessor: (s: any) => {
        const br: billingRate = s;
        return br.rate;
      },
      disableSortBy: true,
      sortable: false,
    },
    {
      Header: 'Start Date',
      accessor: (s: any) => {
        const br: billingRate = s;
        if (moment(br.startDate).isValid()) {
          return moment(br.startDate).format('MM/DD/YYYY');
        }

        return 'N/A';
      },
      disableSortBy: true,
      sortable: false,
    },
    {
      Header: 'End Date',
      accessor: (s: any) => {
        const br: billingRate = s;
        if (moment(br.endDate).isValid()) {
          return moment(br.endDate).format('MM/DD/YYYY');
        }

        return 'N/A';
      },
      disableSortBy: true,
      sortable: false,
    },
    {
      Header: 'Actions',
      accessor: (s: any) => (
        <>
          <button
            className='mr-3 primary'
            onClick={(e) => {
              approve.run(s.billingRateId);
              e.stopPropagation();
            }}
          >
            Approve
          </button>
          <button
            className="bg-red-600 hover:bg-red-700"
            onClick={(e) => {
              setSelectedRateId(s.billingRateId);
              setOpenRejectConfirmModal(true);
              e.stopPropagation();
            }}
          >
            Reject
          </button>
        </>
      ),
      disableSortBy: true,
      sortable: false,

    },
    {
      accessor: 'billingRateId',
      className: 'hidden',
    },
  ];

  //Approval code
  const approve = useAsync({
    deferFn: approveRate,
    onResolve: (response: any) => {
      if (currentUser && currentUser.id && Number.isInteger(Number(currentUser.id))) {
        pendingRatesData.run(currentUser.id);
      }
    },
    onReject: (data) => { console.log(data) }
  });

  const reject = useAsync({
    deferFn: rejectRate,
    onResolve: (response: any) => {
      //Set the notification message
      setMessage('');
      setError('');
      setOpenRejectConfirmModal(false);
      setFeedback('');
      if (currentUser && currentUser.id && Number.isInteger(Number(currentUser.id))) {
        pendingRatesData.run(currentUser.id);
      }
    }
  });

  if (error) return <LayoutError />;

  return (
    <Layout isLoading={
      pendingRatesData.isPending ||
      approve.isPending ||
      reject.isPending
    }>
      <>
        {message !== '' && <Notification>{message}</Notification>}
        {error !== '' && <Notification>{error}</Notification>}
        <EditModal
          open={false}
          saveText='Confirm'
          onSave={() => { reject.run(selectedRateId); }}
          onCancel={() => { setOpenRejectConfirmModal(false) }}
          id={selectedRateId}
        ><h3>Are you sure you would like to reject this billing rate?</h3>
        </EditModal>
        <EditModal
          open={openRejectConfirmModal}
          allowSave={false}
          saveColor='bg-green-500 hover:bg-green-500'
          removeText='Reject'
          removeColor='text-white bg-red-500 hover:bg-red-500'
          onRemove={() => {
            reject.run(selectedRateId, feedback);
          }}
          onCancel={() => { setFeedback(''); setOpenRejectConfirmModal(false) }}
        >
          <Fragment>
            <h3>Reject Changes</h3>
            <Fragment>
              <div className=' mt-2'>

                <h3 className='mt-2 mb-2'>Are you sure you would like to reject this payrate?</h3>

                <div className='w-full mt-2 mb-2'>
                  <div className='flex-column w-full'>
                    <div className='font-bold text-sm'>Feedback</div>
                    <div className='w-full'>
                      <textarea
                        className='w-full'
                        onChange={(e) => { setFeedback(e.currentTarget.value) }}
                        value={feedback ?? ''}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>

          </Fragment>
        </EditModal>
        <div className='grid grid-cols-5 gap-5 mb-6'>
          <div className='col-span-5 sm:col-span-5'>
            <div className='overflow-hidden border-b border-gray-200 rounded-lg therapistsApprove datatable-editable'>
              {pendingRates.length > 0 && (
                <DataTable
                  key={'pc-datatable'}
                  columns={columns()}
                  data={pendingRates}
                  showPagination={true}
                  getRowProps={(row: any) => ({
                    className: "cursor-pointer hover:bg-gray-100",
                  })}
                />
              )}
              {(pendingRates.length === 0 || pendingRates === undefined) && <h3>No pending rates were found for this user</h3>}
            </div>
          </div>
        </div>
      </>
    </Layout>
  );
};

export default ApproveRates;